/**
 * Shared Formatting Utilities
 *
 * Consolidated formatting functions for prices, dates, and status displays.
 *
 * @file formatters.ts
 * @version 1.0.0
 */

// ============================================================================
// PRICE FORMATTING
// ============================================================================

/**
 * Format price from minor units (cents/öre) to display string.
 *
 * @param minorUnits - Price in minor units (e.g., 29900 for 299.00)
 * @param currency - ISO 4217 currency code (e.g., "SEK", "EUR", "USD")
 * @param locale - Optional locale (defaults to 'sv-SE')
 * @returns Formatted price string (e.g., "299,00 kr")
 *
 * @example
 * formatPrice(29900, 'SEK') // "299,00 kr"
 * formatPrice(9999, 'EUR', 'de-DE') // "99,99 €"
 * formatPrice(1999, 'USD', 'en-US') // "$19.99"
 */
export function formatPrice(minorUnits: number, currency = 'USD', locale = 'sv-SE'): string {
  const majorUnits = minorUnits / 100;

  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency || 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(majorUnits);
  } catch {
    // Fallback if currency is invalid
    return `${majorUnits.toFixed(2)} ${currency}`;
  }
}

/**
 * Format price or show "Free" if zero
 *
 * @param minorUnits - Price in minor units
 * @param currency - ISO 4217 currency code
 * @param locale - Optional locale
 * @returns Formatted price or "Free"
 */
export function formatPriceOrFree(minorUnits: number, currency = 'USD', locale = 'sv-SE'): string {
  if (minorUnits <= 0) {
    return locale.startsWith('sv') ? 'Gratis' : 'Free';
  }
  return formatPrice(minorUnits, currency, locale);
}

/**
 * Options for formatting currency amounts.
 */
interface FormatCurrencyOptions {
  /** Minimum fraction digits (default: 0) */
  minimumFractionDigits?: number;
  /** Maximum fraction digits (default: 2) */
  maximumFractionDigits?: number;
  /** Locale for formatting (default: 'en-US') */
  locale?: string;
}

/**
 * Format currency amount (already in major units like SEK, not öre).
 * Use this when the amount is already in major currency units.
 *
 * @param amount - Amount in major units (e.g., 299.00 for 299 SEK)
 * @param currency - ISO 4217 currency code (e.g., "SEK", "EUR", "USD")
 * @param options - Formatting options (locale, fraction digits)
 * @returns Formatted currency string (e.g., "kr 299" or "$299.00")
 *
 * @example
 * formatCurrency(299) // "SEK 299"
 * formatCurrency(299, 'SEK') // "SEK 299"
 * formatCurrency(99.99, 'EUR', { locale: 'de-DE' }) // "99,99 €"
 * formatCurrency(299, 'SEK', { minimumFractionDigits: 2 }) // "SEK 299.00"
 */
export function formatCurrency(
  amount: number,
  currency = 'USD',
  options: FormatCurrencyOptions = {}
): string {
  const { minimumFractionDigits = 0, maximumFractionDigits = 2, locale = 'en-US' } = options;

  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency || 'USD',
      minimumFractionDigits,
      maximumFractionDigits,
    }).format(amount);
  } catch {
    return `${amount.toFixed(maximumFractionDigits)} ${currency}`;
  }
}

// ============================================================================
// DATE FORMATTING
// ============================================================================

interface DateFormatOptions {
  includeTime?: boolean;
  locale?: string;
  fallback?: string;
}

/**
 * Format date string to localized display format.
 *
 * @param dateString - ISO date string or undefined
 * @param options - Formatting options
 * @returns Formatted date string or fallback
 *
 * @example
 * formatDate('2024-01-15T10:30:00Z') // "15 jan. 2024"
 * formatDate('2024-01-15T10:30:00Z', { includeTime: true }) // "15 jan. 2024, 10:30"
 * formatDate(undefined, { fallback: 'Not set' }) // "Not set"
 */
export function formatDate(
  dateString: string | undefined | null,
  options: DateFormatOptions = {}
): string {
  const { includeTime = false, locale = 'sv-SE', fallback = 'N/A' } = options;

  if (!dateString) return fallback;

  try {
    const formatOptions: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    };

    if (includeTime) {
      formatOptions.hour = '2-digit';
      formatOptions.minute = '2-digit';
    }

    return new Date(dateString).toLocaleDateString(locale, formatOptions);
  } catch {
    return fallback;
  }
}

// ============================================================================
// STATUS FORMATTING
// ============================================================================

/**
 * Format status text for display (e.g., "IN_PRODUCTION" → "In Production")
 *
 * @param status - Status string
 * @returns Formatted status string
 */
export function formatStatus(status: string): string {
  return status
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

// ============================================================================
// VAT CALCULATIONS
// ============================================================================

/**
 * Calculate VAT amount from gross price (inclusive of VAT)
 *
 * @param grossPrice - Price including VAT in minor units
 * @param vatRate - VAT rate as decimal (e.g., 0.25 for 25%)
 * @returns VAT amount in minor units, rounded to integer
 */
export function calculateVAT(grossPrice: number, vatRate: number): number {
  if (grossPrice === 0 || vatRate === 0) return 0;
  // Gross = Net * (1 + Rate)
  // VAT = Gross - Net = Gross - (Gross / (1 + Rate))
  const vatAmount = grossPrice - grossPrice / (1 + vatRate);
  return Math.round(vatAmount);
}

/**
 * Format VAT display string
 *
 * @param amount - VAT amount in minor units
 * @param rate - VAT rate as decimal
 * @param currency - Currency code
 * @param locale - Locale
 */
export function formatVAT(
  amount: number,
  rate: number,
  currency = 'SEK',
  locale = 'sv-SE'
): string {
  const formattedAmount = formatPrice(amount, currency, locale);
  const percentage = Math.round(rate * 100);
  const label = locale.startsWith('sv') ? 'moms' : 'VAT';

  return `${formattedAmount} (${percentage}% ${label})`;
}

/**
 * Format date with long month name
 */
export function formatDateLong(dateString: string | undefined | null, locale = 'sv-SE'): string {
  if (!dateString) return 'N/A';

  try {
    return new Date(dateString).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return 'N/A';
  }
}
